import { MoreVertOutlined } from "@mui/icons-material";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import Beneficiar from "assets/img/svg/benificiar";
import classNames from "classnames";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { NavigationPortalType } from "../types";
import CompanySwitcher from "./CompanySwitcher";

interface Company {
    companyId: number;
    name: string;
    avatarUrl?: string | null;
}

interface NavigationProfileProps {
    /** User display name */
    userName?: string;
    /** Company/Organization name */
    companyName?: string;
    /** Avatar URL */
    avatarUrl?: string;
    /** Portal type */
    portalType: NavigationPortalType;
    /** Whether to show company switcher */
    showCompanySwitcher?: boolean;
    /** List of companies for switcher */
    companies?: Company[];
    /** Current company ID */
    currentCompanyId?: number;
    /** Callback for company switch */
    onSwitchCompany?: (companyId: number, shouldReload: boolean) => void;
    /** Callback for logout */
    onLogout: () => void;
    /** Whether the navigation is collapsed */
    isCollapsed?: boolean;
}

const menuItemStyle = {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 400,
    paddingBlock: "8px",
    transition: "all 0.2s ease",
};

/**
 * Navigation profile section with user info, company switcher, and logout
 * Shows only avatar when collapsed, with tooltip showing user & company name
 */
const NavigationProfile = ({
    userName,
    companyName,
    avatarUrl,
    portalType,
    showCompanySwitcher = false,
    companies = [],
    currentCompanyId,
    onSwitchCompany,
    onLogout,
    isCollapsed = false,
}: NavigationProfileProps) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const companyListRef = useRef<HTMLUListElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
        setAnchorEl(event.currentTarget as HTMLElement);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuKeydown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") return;
        // Allow typing in search field
    };

    const handleLogout = () => {
        handleClose();
        onLogout();
    };

    const showCompanyName = portalType !== "beneficiary" && companyName;

    // Build tooltip content for collapsed state (left aligned)
    const collapsedTooltip = (
        <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: 500 }}>{userName || t("NavigationBar.NoName")}</div>
            {showCompanyName && <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "2px" }}>{companyName}</div>}
        </div>
    );

    const profileClassName = classNames("navigation-profile", {
        "navigation-profile--collapsed": isCollapsed,
    });

    // Same structure for both states - just hide elements with opacity when collapsed
    const profileContent = (
        <div className={profileClassName} onClick={isCollapsed ? handleClick : undefined}>
            <div className="navigation-profile__left">
                <div className="navigation-profile__logo">
                    {avatarUrl ? <img src={avatarUrl} alt="avatar" /> : <Beneficiar />}
                </div>
                <div className="navigation-profile__info">
                    <div title={userName || t("NavigationBar.NoName")} className="navigation-profile__username">
                        {userName}
                    </div>
                    {showCompanyName && (
                        <div title={companyName} className="navigation-profile__company">
                            {companyName}
                        </div>
                    )}
                </div>
            </div>
            <div className="navigation-profile__settings">
                <IconButton onClick={handleClick}>
                    <MoreVertOutlined />
                </IconButton>
            </div>
        </div>
    );

    return (
        <>
            <Tooltip
                title={isCollapsed ? collapsedTooltip : ""}
                placement="right"
                arrow
                disableHoverListener={!isCollapsed}
            >
                {profileContent}
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                elevation={0}
                className="mui-menu"
                sx={
                    showCompanySwitcher
                        ? {
                              "& .MuiList-root": { paddingBlock: 0 },
                              "& .MuiPaper-root": { maxHeight: "190px" },
                          }
                        : { ".MuiList-root": { paddingBlock: 0 } }
                }
                anchorOrigin={{
                    vertical: "top",
                    horizontal: isCollapsed ? "right" : "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                onKeyDown={handleMenuKeydown}
                MenuListProps={{
                    ref: companyListRef,
                }}
            >
                {showCompanySwitcher && onSwitchCompany && (
                    <CompanySwitcher
                        companies={companies}
                        currentCompanyId={currentCompanyId}
                        onSwitch={onSwitchCompany}
                    />
                )}
                <MenuItem
                    onClick={handleLogout}
                    sx={
                        showCompanySwitcher
                            ? {
                                  position: "sticky",
                                  bottom: "0",
                                  backgroundColor: "#fff",
                                  borderTop: "1px solid #eaeaea",
                                  fontFamily: "Poppins",
                                  "&:hover": { backgroundColor: "#eaeaea" },
                              }
                            : menuItemStyle
                    }
                >
                    {t("NavigationBar.Logout")}
                </MenuItem>
            </Menu>
        </>
    );
};

export default NavigationProfile;
